/**
 * RedeemCodeForm — enter a one-time credit code (AT-XXXX-XXXX) and redeem it.
 * Codes arrive by email (signup, purchase, feedback rewards). On success the
 * caller gets the new balance via onRedeemed.
 */
import { useState } from 'react';

import { BackendAPI } from '../../lib/api';
import type { ApiError } from '../../types';
import { Button } from './ui/button';
import { Input } from './ui/input';

/** Props for {@link RedeemCodeForm}. */
export interface RedeemCodeFormProps {
  /** Called with the new balance after a successful redeem. */
  onRedeemed?: (_balance: number) => void;
  /** Prefill the input (e.g. from the purchase success page). */
  initialCode?: string;
}

/** Inline code-redemption form with status messaging. */
export default function RedeemCodeForm({ onRedeemed, initialCode }: RedeemCodeFormProps) {
  const [code, setCode] = useState(initialCode ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const redeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setMessage({ tone: 'bad', text: 'Enter a code — they look like AT-XXXX-XXXX.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const res = await BackendAPI.redeemCode(trimmed);
      if (res.status === 'already_redeemed_self') {
        setMessage({ tone: 'ok', text: 'That code is already applied to your account.' });
      } else {
        const n = res.credits ?? 0;
        // Show the increment (before -> after) so the user sees it actually
        // happened, not just a final number they can't be sure changed.
        const before = res.balance - n;
        setMessage({
          tone: 'ok',
          text: `Code redeemed — ${n} ${n === 1 ? 'credit' : 'credits'} added. Balance: ${before} → ${res.balance} (+${n}).`,
        });
      }
      setCode('');
      onRedeemed?.(res.balance);
    } catch (err) {
      const apiErr = err as ApiError;
      setMessage({
        tone: 'bad',
        text:
          apiErr.status === 409
            ? 'That code has already been used.'
            : apiErr.message || 'Could not redeem the code.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="redeem-code-form">
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="AT-XXXX-XXXX"
          aria-label="Credit code"
          className="font-[var(--font-mono)] tracking-[.06em]"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void redeem();
          }}
          disabled={busy}
        />
        <Button variant="secondary" onClick={() => void redeem()} disabled={busy}>
          {busy ? 'Redeeming…' : 'Redeem'}
        </Button>
      </div>
      {message && (
        <p
          className="mt-2 text-[12px]"
          style={{ color: message.tone === 'ok' ? 'var(--ok)' : 'var(--bad)' }}
          role="status"
        >
          {message.text}
        </p>
      )}
    </div>
  );
}
